/**
 * Visual editor for tab schemas (Finance / Démarches / Installation / Compta / Documents / Vérif docs).
 * - Tab selector (6 tabs)
 * - For "installation": produit sub-selector (base / PV / ITE / PAC Air-Air / PAC Air-Eau)
 * - Fields editor: add / rename / set type (text/number/date/select/textarea) / options / status (libre/dropdown)
 * - Workflow editor: add / rename / reorder (up/down)
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, ArrowUp, ArrowDown, Settings2 } from "lucide-react";
import { toast } from "sonner";
import AppLayout, { PageHeader } from "../components/AppLayout";
import { api } from "../lib/api";

const TABS = [
  { key: "synthese", label: "Synthèse" },
  { key: "verif_documents", label: "Vérif documents" },
  { key: "finance", label: "Finance" },
  { key: "demarches", label: "Démarches" },
  { key: "installation", label: "Installation" },
  { key: "comptabilite", label: "Comptabilité" },
  { key: "sav", label: "SAV" },
  { key: "desinstallation", label: "Désinstallation" },
  { key: "documents", label: "Dossier (documents)" },
];
const INSTALLATION_VARIANTS = ["(base)", "PV", "ITE", "PAC"];
const FIELD_TYPES = ["text", "number", "amount", "date", "select", "textarea", "checkbox", "email", "phone", "document", "files", "call"];
const TYPE_FR = { text: "Texte", number: "Nombre", amount: "Montant (€)", date: "Date", select: "Liste de choix", textarea: "Texte long", checkbox: "Oui / non",
  email: "Email", phone: "Téléphone", document: "Emplacement de pièce", files: "Fichiers", call: "Bouton d'appel", materiel: "Matériel PV", datetime: "Date et heure" };
const PHASES = ["À la réception", "Avant la pose", "Après la pose"];
const PRODS = ["PV", "ITE", "PAC"];

function FieldOptions({ field, update, tab, idx, inVariant }) {
  const prods = field.produits || [];
  const small = "px-2 py-1 text-xs border border-norea-border rounded";
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-norea-textMuted" data-testid={`field-options-row-${idx}`}>
      <input value={field.help || ""} onChange={(e) => update({ help: e.target.value })} className={`${small} w-56`} placeholder="Aide (affichée sous le champ)" data-testid={`field-help-${idx}`} />
      {!["document", "files", "call"].includes(field.type) && <input value={field.default ?? ""} onChange={(e) => update({ default: e.target.value || null })} className={`${small} w-36`} placeholder="Valeur par défaut" data-testid={`field-default-${idx}`} />}
      <input value={field.section || ""} onChange={(e) => update({ section: e.target.value || null })} className={`${small} w-36`} placeholder="Section" data-testid={`field-section-${idx}`} />
      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!field.required} onChange={(e) => update({ required: e.target.checked })} data-testid={`field-required-${idx}`} /> Obligatoire</label>
      {!inVariant && <span className="inline-flex items-center gap-2">Produits :{PRODS.map((p) => (
        <label key={p} className="inline-flex items-center gap-0.5"><input type="checkbox" checked={!prods.length || prods.includes(p)} onChange={(e) => {
          const cur = prods.length ? prods : PRODS;
          const next = e.target.checked ? [...new Set([...cur, p])] : cur.filter((x) => x !== p);
          update({ produits: next.length === PRODS.length ? [] : next });
        }} data-testid={`field-prod-${p}-${idx}`} />{p}</label>
      ))}</span>}
      <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!field.hidden} onChange={(e) => update({ hidden: e.target.checked })} data-testid={`field-hidden-${idx}`} /> Masqué</label>
      {tab === "installation" && <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!!field.in_om} onChange={(e) => update({ in_om: e.target.checked })} data-testid={`field-in-om-${idx}`} /> Afficher dans l'ordre de mission</label>}
      {field.type === "document" && (
        <select value={field.phase || ""} onChange={(e) => update({ phase: e.target.value || null })} className={small} data-testid={`field-phase-${idx}`}>
          <option value="">Phase : —</option>{PHASES.map((p) => <option key={p}>{p}</option>)}
        </select>
      )}
      <span className="font-mono text-[10px]">{`{${tab}.${field.id}}`}</span>
    </div>
  );
}

function slugify(label) {
  return (label || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || `f_${Date.now().toString(36)}`;
}

function FieldRow({ field, onChange, onDelete, onUp, onDown, isFirst, isLast, idx, tab, drag, inVariant }) {
  const update = (patch) => onChange({ ...field, ...patch });
  return (
    <div className={`p-3 border rounded bg-white space-y-2 ${field.hidden ? "border-dashed opacity-70" : "border-norea-border"} ${drag.over ? "ring-2 ring-norea-primary" : ""}`} data-testid={`field-row-${idx}`}
      draggable onDragStart={drag.start} onDragOver={(e) => { e.preventDefault(); drag.enter(); }} onDrop={drag.drop}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] text-norea-textMuted w-6 cursor-grab" title="Glisser pour déplacer">⋮⋮{idx + 1}</span>
        {field.system && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200" title="Utilisé par les documents : renommable, déplaçable, masquable, mais pas supprimable" data-testid={`field-system-${idx}`}>Système</span>}
        <input
          value={field.label}
          onChange={(e) => update({ label: e.target.value, id: field._new ? (slugify(e.target.value) || field.id) : field.id })}
          className="flex-1 px-2 py-1 text-sm border border-norea-border rounded"
          placeholder="Libellé du champ"
          data-testid={`field-label-${idx}`}
        />
        <select value={field.type || "text"} onChange={(e) => update({ type: e.target.value })} className="text-xs px-1.5 py-1 border border-norea-border rounded" data-testid={`field-type-${idx}`}>
          {[...new Set([...FIELD_TYPES, field.type || "text"])].map((t) => <option key={t} value={t}>{TYPE_FR[t] || t}</option>)}
        </select>
        <select value={field.status_type || "libre"} onChange={(e) => update({ status_type: e.target.value })} className="text-xs px-1.5 py-1 border border-norea-border rounded" data-testid={`field-status-type-${idx}`}>
          <option value="libre">statut libre</option>
          <option value="dropdown">statut dropdown</option>
        </select>
        <div className="flex items-center gap-0.5">
          <button onClick={onUp} disabled={isFirst} className="p-1 text-norea-textMuted hover:text-norea-text disabled:opacity-30" data-testid={`field-up-${idx}`}><ArrowUp className="w-3 h-3" /></button>
          <button onClick={onDown} disabled={isLast} className="p-1 text-norea-textMuted hover:text-norea-text disabled:opacity-30" data-testid={`field-down-${idx}`}><ArrowDown className="w-3 h-3" /></button>
          <button onClick={onDelete} disabled={field.system} title={field.system ? "Champ système : masquez-le plutôt" : "Supprimer"} className="p-1 text-norea-textMuted hover:text-red-600 disabled:opacity-30" data-testid={`field-delete-${idx}`}><Trash2 className="w-3 h-3" /></button>
        </div>
      </div>
      <FieldOptions field={field} update={update} tab={tab} idx={idx} inVariant={inVariant} />
      {field.type === "select" && (
        <input
          value={(field.options || []).join(" | ")}
          onChange={(e) => update({ options: e.target.value.split("|").map((s) => s.trim()).filter(Boolean) })}
          className="w-full px-2 py-1 text-xs border border-norea-border rounded"
          placeholder="Options séparées par | (ex: Comptant | Crédit | Mixte)"
          data-testid={`field-options-${idx}`}
        />
      )}
      {field.status_type === "dropdown" && (
        <input
          value={(field.status_options || []).join(" | ")}
          onChange={(e) => update({ status_options: e.target.value.split("|").map((s) => s.trim()).filter(Boolean) })}
          className="w-full px-2 py-1 text-xs border border-norea-border rounded"
          placeholder="Statuts possibles séparés par | (ex: À faire | OK | KO)"
          data-testid={`field-status-options-${idx}`}
        />
      )}
    </div>
  );
}

function WorkflowRow({ step, onChange, onDelete, onUp, onDown, isFirst, isLast, idx }) {
  return (
    <div className="flex items-center gap-2 p-2 border border-norea-border rounded bg-white" data-testid={`wf-row-${idx}`}>
      <span className="font-mono text-[10px] text-norea-textMuted w-6">#{idx + 1}</span>
      <input
        value={step.label}
        onChange={(e) => onChange({ ...step, label: e.target.value, id: step.id || slugify(e.target.value) })}
        className="flex-1 px-2 py-1 text-sm border border-norea-border rounded"
        placeholder="Nom de l'étape"
        data-testid={`wf-label-${idx}`}
      />
      <button onClick={onUp} disabled={isFirst} className="p-1 text-norea-textMuted hover:text-norea-text disabled:opacity-30"><ArrowUp className="w-3 h-3" /></button>
      <button onClick={onDown} disabled={isLast} className="p-1 text-norea-textMuted hover:text-norea-text disabled:opacity-30"><ArrowDown className="w-3 h-3" /></button>
      <button onClick={onDelete} className="p-1 text-norea-textMuted hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
    </div>
  );
}

export default function SettingsSchemasPage() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "finance");
  const [variant, setVariant] = useState("(base)");
  const [schema, setSchema] = useState(null);
  const [variants, setVariants] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Always load the FULL raw schema (not the merged variant)
      const r = await api.get(`/configuration/tab-schema/${tab}`);
      setSchema({ fields: r.data.fields || [], workflow: r.data.workflow || [] });
      setVariants(r.data.variants || {});
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); setVariant("(base)"); }, [load]);

  const isInstallation = tab === "installation";
  const editingVariant = isInstallation && variant !== "(base)";
  const current = editingVariant ? (variants[variant] || { fields: [], workflow: [] }) : schema;

  const setCurrent = (patch) => {
    if (editingVariant) {
      setVariants((prev) => ({ ...prev, [variant]: { ...(prev[variant] || { fields: [], workflow: [] }), ...patch } }));
    } else {
      setSchema((prev) => ({ ...prev, ...patch }));
    }
  };

  const fields = current?.fields || [];
  const workflow = current?.workflow || [];

  const addField = () => setCurrent({ fields: [...fields, { id: `f_${Date.now().toString(36)}`, _new: true, label: "Nouveau champ", type: "text", status_type: "libre", options: [], status_options: [] }] });
  const addStep = () => setCurrent({ workflow: [...workflow, { id: `s_${Date.now().toString(36)}`, label: "Nouvelle étape" }] });

  const updateField = (idx, newF) => setCurrent({ fields: fields.map((f, i) => (i === idx ? newF : f)) });
  const deleteField = (idx) => setCurrent({ fields: fields.filter((_, i) => i !== idx) });
  const moveField = (idx, delta) => {
    const next = [...fields];
    const [m] = next.splice(idx, 1);
    next.splice(idx + delta, 0, m);
    setCurrent({ fields: next });
  };

  const updateStep = (idx, newS) => setCurrent({ workflow: workflow.map((s, i) => (i === idx ? newS : s)) });
  const deleteStep = (idx) => setCurrent({ workflow: workflow.filter((_, i) => i !== idx) });
  const moveStep = (idx, delta) => {
    const next = [...workflow];
    const [m] = next.splice(idx, 1);
    next.splice(idx + delta, 0, m);
    setCurrent({ workflow: next });
  };

  const save = async () => {
    setSaving(true);
    try {
      const uniq = (fs) => { const seen = new Set(); return fs.map((f) => { let id = f.id || "champ"; while (seen.has(id)) id = `${id}_2`; seen.add(id); const { _new, ...rest } = f; return { ...rest, id }; }); };
      const payload = { fields: uniq(schema?.fields || []), workflow: schema?.workflow || [] };
      if (isInstallation) payload.variants = Object.fromEntries(Object.entries(variants).map(([k, v]) => [k, { ...v, fields: uniq(v.fields || []) }]));
      await api.put(`/configuration/tab-schema/${tab}`, payload);
      toast.success("Schéma enregistré");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayout>
      <PageHeader
        title="Schémas des onglets"
        subtitle="Édite les champs, workflows et logiques conditionnelles affichés dans la vue dossier."
        breadcrumb={[
          { label: "Norea", to: "/" },
          { label: "Configuration", to: "/configuration" },
          "Schémas",
        ]}
        actions={
          <button onClick={save} disabled={saving || loading} className="btn-primary text-xs" data-testid="btn-save-schema">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Enregistrer le schéma
          </button>
        }
      />

      <div className="flex items-center gap-2 flex-wrap mb-4">
        <span className="text-xs text-norea-textMuted">Onglet :</span>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`text-xs px-3 py-1 rounded border ${tab === t.key ? "bg-norea-primary text-white border-norea-primary" : "border-norea-border hover:bg-norea-surfaceHover"}`}
            data-testid={`schema-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isInstallation && (
        <div className="flex items-center gap-2 mb-4 p-2.5 border border-norea-border rounded bg-norea-bg">
          <Settings2 className="w-4 h-4 text-norea-primary" />
          <span className="text-xs text-norea-text font-medium">Variante produit :</span>
          {INSTALLATION_VARIANTS.map((v) => (
            <button
              key={v}
              onClick={() => setVariant(v)}
              className={`text-xs px-2.5 py-1 rounded border ${variant === v ? "bg-norea-primary text-white border-norea-primary" : "border-norea-border hover:bg-norea-surfaceHover"}`}
              data-testid={`schema-variant-${v}`}
            >
              {v}
            </button>
          ))}
          <span className="text-[11px] text-norea-textMuted ml-auto">
            {editingVariant ? `Les champs ci-dessous s'affichent QUAND produit = ${variant}` : "Champs partagés par toutes les variantes"}
          </span>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-sm font-semibold text-norea-text">Champs ({fields.length})</h3>
              <button onClick={addField} className="btn-secondary text-xs" data-testid="btn-add-field">
                <Plus className="w-3 h-3" /> Ajouter un champ
              </button>
            </div>
            {fields.length === 0 ? (
              <div className="p-4 text-center text-xs text-norea-textMuted italic">Aucun champ — ajoute le premier ci-dessus.</div>
            ) : (
              <div className="space-y-2">
                {fields.map((f, idx) => (
                  <FieldRow
                    key={f.id || idx}
                    field={f}
                    inVariant={editingVariant}
                    idx={idx}
                    isFirst={idx === 0}
                    isLast={idx === fields.length - 1}
                    onChange={(nf) => updateField(idx, nf)}
                    onDelete={() => deleteField(idx)}
                    onUp={() => moveField(idx, -1)}
                    onDown={() => moveField(idx, 1)}
                    tab={tab}
                    drag={{ over: dragOver === idx, start: () => setDragFrom(idx), enter: () => setDragOver(idx), drop: () => { if (dragFrom !== null && dragFrom !== idx) moveField(dragFrom, idx - dragFrom); setDragFrom(null); setDragOver(null); } }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="card-flat p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-sm font-semibold text-norea-text">Étapes de workflow ({workflow.length})</h3>
              <button onClick={addStep} className="btn-secondary text-xs" data-testid="btn-add-step">
                <Plus className="w-3 h-3" /> Ajouter une étape
              </button>
            </div>
            {workflow.length === 0 ? (
              <div className="p-4 text-center text-xs text-norea-textMuted italic">Aucune étape — ajoute la première ci-dessus.</div>
            ) : (
              <div className="space-y-2">
                {workflow.map((s, idx) => (
                  <WorkflowRow
                    key={s.id || idx}
                    step={s}
                    idx={idx}
                    isFirst={idx === 0}
                    isLast={idx === workflow.length - 1}
                    onChange={(ns) => updateStep(idx, ns)}
                    onDelete={() => deleteStep(idx)}
                    onUp={() => moveStep(idx, -1)}
                    onDown={() => moveStep(idx, 1)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
